import { useSendChatMessage } from '@/wabunka-src/features/Chat/composables/mutation/useSendChatMessage';
import type { ChatMessage, ConversationHistoryItem } from '@/wabunka-src/features/Chat/models/chatMessage';
import { createAssistantMessageFromApiResponse, createErrorMessage, createUserMessage, createWelcomeMessage } from '@/wabunka-src/features/Chat/models/chatMessageFactory';

const MAX_CONVERSATION_HISTORY = 10;

/** タイムアウトエラー判定 (AbortSignal.timeout は TimeoutError を投げる) */
const isTimeoutError = (error: unknown): boolean => {
  return error instanceof DOMException && error.name === 'TimeoutError';
};

/** エラーステータスに応じたユーザー向けメッセージ */
export const getErrorMessageText = (status: number | undefined): string => {
  switch (status) {
    case 400:
      return "Sorry, I couldn't process your message. Please try again.";
    case 429:
      return "You've reached the daily message limit. Please try again tomorrow.";
    case 408:
      return 'The response is taking too long. Please try again with a shorter message.';
    case 500:
    case 502:
      return 'Something went wrong. Please try again later.';
    default:
      return 'Something went wrong. Please try again later.';
  }
};

/**
 * メッセージ状態管理 + APIレスポンス→ブロック変換
 */
export const useChatMessages = () => {
  const messages = ref<ChatMessage[]>([createWelcomeMessage()]);
  const isLoading = ref(false);
  const isRateLimited = ref(false);

  const { send } = useSendChatMessage();

  /** メッセージを追加する */
  const addMessage = (message: ChatMessage) => {
    messages.value = [...messages.value, message];
  };

  /** 会話履歴を生成する（API送信用、直近MAX_CONVERSATION_HISTORY件） */
  const conversationHistory = computed<ConversationHistoryItem[]>(() => {
    return messages.value
      .filter((msg): msg is ChatMessage & { rawContent: string } => msg.includeInHistory && Boolean(msg.rawContent))
      .map((msg) => ({
        role: msg.role,
        content: msg.rawContent,
      }))
      .slice(-MAX_CONVERSATION_HISTORY);
  });

  /**
   * メッセージを送信してAIレスポンスを取得する
   */
  const sendMessage = async (text: string, sessionId: string, slug: string) => {
    if (isLoading.value || isRateLimited.value) return;

    // 今回送る user メッセージは API リクエストの `message` フィールドに乗るため、
    // history からは除外する（addMessage 前にスナップショットを取る）。
    const historyToSend = conversationHistory.value;
    const userMessage = createUserMessage(text);
    addMessage(userMessage);
    isLoading.value = true;

    try {
      const response = await send({
        message: text,
        sessionId,
        slug,
        conversationHistory: historyToSend,
      });

      const assistantMessage = createAssistantMessageFromApiResponse(response, slug);
      addMessage(assistantMessage);
    } catch (error: unknown) {
      // 失敗した user 発話は LLM 履歴に残さない（assistant ターンが欠けて user が連続する歪みを防ぐ）。
      // 画面には残るので、ユーザー側のスクロールバックには影響しない。
      userMessage.includeInHistory = false;

      if (isTimeoutError(error)) {
        addMessage(createErrorMessage(getErrorMessageText(408), slug, false));
        return;
      }

      const fetchError = error as { statusCode?: number };
      const status = fetchError.statusCode;
      const errorText = getErrorMessageText(status);

      if (status === 429) {
        isRateLimited.value = true;
      }

      addMessage(createErrorMessage(errorText, slug, false));
    } finally {
      isLoading.value = false;
    }
  };

  /** 会話をリセットする */
  const resetMessages = () => {
    messages.value = [createWelcomeMessage()];
    isLoading.value = false;
    isRateLimited.value = false;
  };

  return {
    messages: readonly(messages),
    isLoading: readonly(isLoading),
    isRateLimited: readonly(isRateLimited),
    sendMessage,
    resetMessages,
    conversationHistory,
  };
};
